<template>
	<div class="buttons">
		<div class="buttons__left">
			<Z-icon-button :icon="icons.download" class="container__header__right__btn" :disabled="!request" :loading="loadingPdfDownload" @click="onCopyPrintResult('pdf', 'download')">{{ $t('Stiahnuť PDF') }}</Z-icon-button>
			<Z-icon-button :icon="icons.printer" class="container__header__right__btn" :disabled="!request" :loading="loadingPdfOpen" @click="onCopyPrintResult('pdf', 'open')">{{ $t('Vytlačiť') }}</Z-icon-button>

			<A-dropdown :options="copyOptions" :disabled="!request" :loading="loadingCopy" />
		</div>

		<div class="buttons__right">
			<div class="mr-[-12px]">
				<Z-icon-button :icon="request?.is_readed ? icons.seen : icons.unseen" :disabled="!request" @click="toggleReadUnread">
					{{ request?.is_readed ? $t('Označiť ako neprečítané') : $t('Označiť ako prečítané') }}
				</Z-icon-button>
			</div>
		</div>

		<ion-fab v-if="request" slot="fixed" vertical="center" horizontal="end">
			<ion-fab-button size="small">
				<ion-icon :icon="chevronDownOutline"></ion-icon>
			</ion-fab-button>

			<ion-fab-list side="bottom">
				<ion-fab-button @click="onCopyPrintResult('pdf', 'download')">
					<ion-icon :icon="icons.download" color="primary"></ion-icon>
				</ion-fab-button>
				<ion-fab-button @click="onCopyPrintResult('pdf', 'open')">
					<ion-icon :icon="icons.printer" color="primary"></ion-icon>
				</ion-fab-button>
				<ion-fab-button @click="onCopyPrintResult('txt')">
					<ion-icon :icon="icons.copy" color="primary"></ion-icon>
				</ion-fab-button>
			</ion-fab-list>
		</ion-fab>
	</div>

	<div class="horizontal-line" />

	<div class="header">
		<div class="header__title">
			<h2 v-if="request">
				<b>{{ $t('Výsledok k žiadanke č.') }} {{ request?.examination_request?.barcode || request?.order_id }}</b>
			</h2>
			<div v-else class="skelet w-[300px] h-[30px] mr-[10px] inline-block"></div>
		</div>

		<div class="header__creation-dates">
			<template v-if="request">
				<Z-status-badge v-if="request.order_state" :status="request.order_state" :statusMap="statusMap" />

				<p class="medium">{{ $t('Žiadanka odoslaná ') + formatDate(request.examination_request?.creation_time) + $t(' o ') + formatTime(request.examination_request?.creation_time) }}</p>
				<p v-if="request.examination_request?.sampling_time" class="medium">{{ $t('Odber vykonaný') }} {{ formatDate(request.examination_request?.sampling_time) + $t(' o ') + formatTime(request.examination_request?.sampling_time) }}</p>
			</template>

			<template v-else>
				<div class="skelet w-[240px] h-[30px] mb-[5px]"></div>
				<div class="skelet w-[240px] h-[18px] mb-[5px]"></div>
				<div class="skelet w-[240px] h-[18px] mb-[5px]"></div>
			</template>
		</div>
	</div>
</template>

<script>
import { Clipboard } from '@capacitor/clipboard'
import { formatDate, formatTime } from '@/plugins/app/utils/date.utils'
import { copy, printer, download, seen, unseen } from '@/plugins/@medirex/icons'
import { chevronDownOutline } from 'ionicons/icons'
import { confirmModalController } from '@medirex/vue-components'
import ADropdown from './a-dropdown.vue'
import copyTable from '../_assets/copy-table.png'
import copyText from '../_assets/copy-text.png'

import CopyPrintMixin from '../../_mixins/copy-print.mixin'
import RequestStatuses from '@/plugins/app@lz/request/_mixins/request-statuses.mixin'
import ZStatusBadge from '@/plugins/app@components/status-badge/z-status-badge.vue'

export default {
	mixins: [CopyPrintMixin, RequestStatuses],
	emits: ['toggle-read-unread'],
	components: { ADropdown, ZStatusBadge },

	props: {
		request: {
			type: Object,
			default: () => null
		}
	},

	setup() {
		return {
			icons: {
				download,
				copy,
				seen,
				printer,
				unseen
			}
		}
	},

	data() {
		return {
			chevronDownOutline,
			formatDate,
			formatTime,
			loadingCopy: false,
			loadingPdfOpen: false,
			loadingPdfDownload: false,
			copyOptions: [
				{
					title: this.$t('Kopírovať v tabuľke'),
					description: this.$t('Text bude po vložení v tabuľke.'),
					image: copyTable,
					handler: () => {
						this.onCopyPrintResult('txt')
					}
				},
				{
					title: this.$t('Kopírovať skrátený'),
					description: this.$t('Text bude po vložení v riadkoch.'),
					image: copyText,
					handler: () => {
						this.onCopyPrintResult('txt_condense')
					}
				},
				{
					title: this.$t('Kopírovať ako CSV'),
					description: this.$t('Text bude po vložení vo formáte CSV.'),
					image: copyText,
					handler: () => {
						this.onCopyPrintResult('csv')
					}
				}
			]
		}
	},

	computed: {
		isSafari() {
			const ua = navigator.userAgent
			// Overenie, či ide o Safari (a nie Chrome alebo iný prehliadač)
			return ua.includes('Safari') && !ua.includes('Chrome')
		}
	},

	methods: {
		toggleReadUnread() {
			this.$emit('toggle-read-unread')
		},

		async onCopyPrintResult(format = 'txt', action = 'copy') {
			const orderExternalId = this.request.order_id

			try {
				if (format == 'txt' || format == 'txt_condense' || format == 'csv') {
					this.loadingCopy = true
				} else if (format == 'pdf' && action == 'open') {
					this.loadingPdfOpen = true
				} else if (format == 'pdf' && action == 'download') {
					this.loadingPdfDownload = true
				}

				const resultIds = this.request.results.map((result) => result.full_result_id)
				// const orderExternalId = this.request.order_id

				let filename = `${this.request.patient.last_name || ''}_${this.request.patient.first_name || ''}_${orderExternalId}`
				filename = filename
					.replace(/[\s.]/g, '_')
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '')
					.toLowerCase()

				if (format == 'csv') {
					const csvString = this.request?.results.map((result) => `${result?.params.map((obj) => `${obj?.short_name ? obj?.short_name : ''};${obj?.value?.value ? obj?.value?.value : this.$t('Neskôr')};${obj?.unit ? obj?.unit : ''}`).join('\n')}`).join('\n')

					await Clipboard.write({ string: csvString })

					this.$toast.info(this.$t('Výsledok bol skopírovaný'))
				} else {
					if (this.isSafari && action == 'copy') {
						const copyData = await this.copyResultData(resultIds, orderExternalId, filename, format, action)
						const modal = await confirmModalController.create({
							text: 'Naozaj chcete skopírovať výsledok?',
							icon: copy,
							confirmText: 'Áno',
							confirmCallback: () => {
								this._resultDataAction(copyData, orderExternalId, filename, action)
							}
						})
						await modal.present()
					} else await this.copyPrintResult(resultIds, orderExternalId, filename, format, action)
				}
			} catch (error) {
				console.error(error)
			} finally {
				if (format == 'txt' || format == 'txt_condense' || format == 'csv') {
					this.loadingCopy = false
				} else if (format == 'pdf' && action == 'open') {
					this.loadingPdfOpen = false
				} else if (format == 'pdf' && action == 'download') {
					this.loadingPdfDownload = false
				}
			}
		}
	}
}
</script>

<style lang="sass" scoped>
@import '@/plugins/app/theme/_mixins/responsive.mixin'
ion-fab
	@apply hidden mr-[-16px]

	+md
		@apply block

.buttons
	@apply w-full min-h-[57px] relative flex items-center justify-between

	&__left
		@apply flex items-center gap-[20px]

		+md
			@apply hidden

.horizontal-line
	@apply border border-b -mr-[30px] -ml-[30px]

	+md
		@apply mx-0

.header
	@apply py-[19px] border-black border-b-2 relative flex items-start justify-between gap-4

	+md
		// @apply block
		@apply flex-wrap

	&__title
		h2
			@apply pr-[10px] text-[24px] inline-block

	&__creation-dates
		display: flex
		flex-direction: column
		align-items: flex-end

		+md
			@apply mt-[-10px] items-start

		:deep(ion-button)
			@apply mb-[10px]

	&__desc
		@apply text-[18px] -mt-[12px]
</style>
